import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const SUBMENU_REALIGN_DELAY = 50;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #layoutRefreshFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#applySubmenuEditorialDescriptions();
    this.#applySubmenuEditorialMedia();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });
    window.addEventListener('resize', this.#debouncedSyncAnchoredSubmenu, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusout', this.#handleFocusOut, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    cancelAnimationFrame(this.#layoutRefreshFrame);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately.
   * Touch and pen pointers use the explicit submenu toggle instead of hover semantics.
   * @param {PointerEvent} event
   */
  activate = (event) => {
    if ('pointerType' in event && event.pointerType !== 'mouse') return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Toggle a submenu from mouse, trackpad, touch, pen, or keyboard activation.
   * Hover-capable pointers can still open submenus through pointerenter.
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const item = event.target;
    if (!(item instanceof HTMLElement)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    if (item === this.#state.activeItem) {
      this.#deactivate(item, true);
      return;
    }

    this.#activateItem(item);
  };

  /**
   * Activate the selected menu item with a delay.
   * @param {PointerEvent} event
   */
  #activateHandler = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#activateItem(item);
  };

  /**
   * Activate a resolved top-level menu item.
   * @param {HTMLElement} item
   */
  #activateItem = (item) => {
    this.#debouncedDeactivate.cancel();
    this.#applySubmenuEditorialDescriptions();
    this.#applySubmenuEditorialMedia();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const isDefaultSlot = item.parentElement?.slot === '';
    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (item == this.#state.activeItem) {
      if (submenu) {
        submenu.dataset.active = '';
        this.#refreshSubmenuLayout(item, submenu);
        this.#scheduleSubmenuLayoutRefresh(item, submenu);
      }

      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
      this.#refreshSubmenuLayout(item, submenu);
      this.#scheduleSubmenuLayoutRefresh(item, submenu);
    }

    if (!submenu) {
      this.style.setProperty('--submenu-height', '0px');
    }

    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay.
   * Touch and pen pointers are closed explicitly by toggle or outside interaction.
   * @param {PointerEvent} event
   */
  deactivate(event) {
    if ('pointerType' in event && event.pointerType !== 'mouse') return;

    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately.
   * Explicit toggle actions can close an open submenu even while its trigger remains hovered.
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    submenu?.style.removeProperty('--submenu-anchor-offset');

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleDocumentPointerDown = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;

    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  #handleFocusOut = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;

    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  #debouncedSyncAnchoredSubmenu = debounce(() => this.#syncAnchoredSubmenu(), SUBMENU_REALIGN_DELAY);

  /**
   * Refresh submenu geometry after layout-affecting enhancement or activation changes.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #refreshSubmenuLayout = (item, submenu) => {
    this.#syncAnchoredSubmenu(item, submenu);
    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  /**
   * Re-run active submenu geometry after the browser has applied content-visibility and editorial media layout.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #scheduleSubmenuLayoutRefresh = (item, submenu) => {
    cancelAnimationFrame(this.#layoutRefreshFrame);

    this.#layoutRefreshFrame = requestAnimationFrame(() => {
      this.#enhanceAndRefreshSubmenuLayout(item, submenu);

      this.#layoutRefreshFrame = requestAnimationFrame(() => {
        this.#enhanceAndRefreshSubmenuLayout(item, findSubmenu(item) || submenu);
      });
    });
  };

  /**
   * Re-apply metadata enhancements against the current DOM before measuring.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #enhanceAndRefreshSubmenuLayout = (item, submenu) => {
    if (item !== this.#state.activeItem) return;

    this.#applySubmenuEditorialDescriptions();
    this.#applySubmenuEditorialMedia();

    const activeSubmenu = findSubmenu(item) || submenu;

    if (!(activeSubmenu instanceof HTMLElement)) return;

    activeSubmenu.dataset.active = '';
    this.#refreshSubmenuLayout(item, activeSubmenu);
  };

  #applySubmenuEditorialDescriptions = () => {
    const editorialMetadata = this.querySelectorAll('[data-submenu-editorial-item]');
    const descriptionMap = new Map();

    editorialMetadata.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const parentMenuItem = item.dataset.parentMenuItem?.trim();
      const submenuItem = item.dataset.submenuItem?.trim();
      const description = item.textContent?.trim();

      if (!parentMenuItem || !submenuItem || !description) return;

      descriptionMap.set(`${parentMenuItem}::${submenuItem}`, description);
    });

    const submenuRows = this.querySelectorAll('.mega-menu__link-item[data-parent-menu-item-key][data-submenu-item-key]');

    submenuRows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;

      const parentMenuItem = row.dataset.parentMenuItemKey?.trim();
      const submenuItem = row.dataset.submenuItemKey?.trim();
      const link = row.querySelector('.mega-menu__link');

      if (!(link instanceof HTMLElement) || !parentMenuItem || !submenuItem) return;

      const description = descriptionMap.get(`${parentMenuItem}::${submenuItem}`) || '';
      const existingDescription = link.querySelector('.mega-menu__link-description');

      if (description) {
        row.classList.add('mega-menu__link-item--described');
        link.classList.add('mega-menu__link--with-description');

        if (existingDescription instanceof HTMLElement) {
          existingDescription.textContent = description;
        } else {
          const descriptionElement = document.createElement('span');
          descriptionElement.className = 'mega-menu__link-description';
          descriptionElement.textContent = description;
          link.append(descriptionElement);
        }
      } else {
        row.classList.remove('mega-menu__link-item--described');
        link.classList.remove('mega-menu__link--with-description');
        existingDescription?.remove();
      }
    });
  };

  #applySubmenuEditorialMedia = () => {
    const editorialMedia = this.querySelectorAll('[data-submenu-editorial-media]');
    const mediaMap = new Map();

    editorialMedia.forEach((item) => {
      if (!(item instanceof HTMLTemplateElement)) return;

      const parentMenuItem = item.dataset.parentMenuItem?.trim();

      if (!parentMenuItem || !item.content.firstElementChild) return;

      mediaMap.set(parentMenuItem, item);
    });

    const submenus = this.querySelectorAll('.menu-list__submenu[data-menu-item-key]');

    submenus.forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;

      const menuItem = submenu.dataset.menuItemKey?.trim();
      const megaMenu = submenu.querySelector('.mega-menu');

      if (!(megaMenu instanceof HTMLElement) || !menuItem) return;

      const existingMedia = megaMenu.querySelector(':scope > .mega-menu__editorial-media');
      const template = mediaMap.get(menuItem);
      const nextMedia = template?.content.firstElementChild;
      const hasMedia = template instanceof HTMLTemplateElement && nextMedia instanceof HTMLElement;

      if (!hasMedia) {
        existingMedia?.remove();
      } else if (existingMedia instanceof HTMLElement && existingMedia.outerHTML !== nextMedia.outerHTML) {
        existingMedia.replaceWith(template.content.cloneNode(true));
      } else if (!(existingMedia instanceof HTMLElement)) {
        megaMenu.append(template.content.cloneNode(true));
      }

      megaMenu.classList.toggle('mega-menu--with-editorial-media', hasMedia);
    });
  };

  /**
   * Align a submenu to its trigger when the menu item opts into anchored positioning.
   * @param {HTMLElement | null} [item]
   * @param {HTMLElement | null} [submenu]
   */
  #syncAnchoredSubmenu = (item = this.#state.activeItem, submenu = item ? findSubmenu(item) : null) => {
    if (!(item instanceof HTMLElement) || !(submenu instanceof HTMLElement)) return;
    if (submenu.dataset.anchorSubmenu !== 'trigger') return;

    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    const megaMenu = submenu.querySelector('.mega-menu');

    if (!(submenuInner instanceof HTMLElement) || !(megaMenu instanceof HTMLElement)) return;

    const submenuInnerStyles = window.getComputedStyle(submenuInner);
    const paddingInlineStart = parseFloat(submenuInnerStyles.paddingInlineStart || '0');
    const paddingInlineEnd = parseFloat(submenuInnerStyles.paddingInlineEnd || '0');
    const currentAnchorOffset = parseFloat(submenu.style.getPropertyValue('--submenu-anchor-offset') || '0');
    const basePaddingInlineStart = Math.max(0, paddingInlineStart - currentAnchorOffset);
    const triggerRect = item.getBoundingClientRect();
    const submenuInnerRect = submenuInner.getBoundingClientRect();
    const megaMenuRect = megaMenu.getBoundingClientRect();
    const minLeft = submenuInnerRect.left + basePaddingInlineStart;
    const maxLeft = Math.max(minLeft, submenuInnerRect.right - paddingInlineEnd - megaMenuRect.width);
    const clampedLeft = Math.min(Math.max(triggerRect.left, minLeft), maxLeft);

    submenu.style.setProperty('--submenu-anchor-offset', `${clampedLeft - minLeft}px`);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Scoped desktop product navigator for the featured-products mega menu.
 *
 * @typedef {object} MegaMenuProductsNavRefs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [items]
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<MegaMenuProductsNavRefs>}
 */
class MegaMenuProductsNav extends Component {
  requiredRefs = ['viewport', 'track', 'previous', 'next'];

  #currentIndex = 0;
  #resizeObserver = new ResizeObserver(() => this.#sync());

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.viewport instanceof HTMLElement) {
      this.#resizeObserver.observe(this.refs.viewport);
    }

    requestAnimationFrame(() => this.#sync());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  get items() {
    return Array.isArray(this.refs.items) ? this.refs.items : [];
  }

  get visibleCount() {
    const computedVisibleCount = parseInt(
      window.getComputedStyle(this).getPropertyValue('--mega-menu-featured-products-active-columns').trim(),
      10
    );
    const fallbackCount = parseInt(this.dataset.visibleCount || '3', 10);
    const count =
      Number.isNaN(computedVisibleCount) || computedVisibleCount < 1 ? fallbackCount : computedVisibleCount;

    return Number.isNaN(count) || count < 1 ? 3 : count;
  }

  get maxIndex() {
    return Math.max(this.items.length - this.visibleCount, 0);
  }

  previous = (event) => {
    event?.preventDefault();
    this.#goTo(this.#currentIndex - 1);
  };

  next = (event) => {
    event?.preventDefault();
    this.#goTo(this.#currentIndex + 1);
  };

  #sync = () => {
    this.#currentIndex = Math.min(this.#currentIndex, this.maxIndex);
    this.#update();
  };

  #goTo(index) {
    this.#currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.#update();
  }

  #update() {
    const { track, previous, next } = this.refs;
    const items = this.items;
    const firstItem = items[0];
    const currentItem = items[this.#currentIndex];

    if (!(track instanceof HTMLElement) || !(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
      return;
    }

    if (!(firstItem instanceof HTMLElement) || !(currentItem instanceof HTMLElement)) {
      track.style.transform = '';
      previous.disabled = true;
      next.disabled = true;
      return;
    }

    const offset = currentItem.offsetLeft - firstItem.offsetLeft;

    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    previous.disabled = this.#currentIndex <= 0;
    next.disabled = this.#currentIndex >= this.maxIndex;
  }
}

if (!customElements.get('mega-menu-products-nav')) {
  customElements.define('mega-menu-products-nav', MegaMenuProductsNav);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
